import { useEffect, useState } from 'react'
import { useSession } from '@/features/auth/hooks/useSession'
import { BillingIssueModal } from './BillingIssueModal'

/**
 * Root-mounted listener (PDR 012, Phase 1) that turns the app-wide
 * `xavior:workspace-locked` event — dispatched from lib/http.ts when a write
 * returns `403 WORKSPACE_LOCKED` — into the BillingIssueModal. Mounted once
 * inside SessionProvider (App.tsx) so useSession resolves the selected
 * workspace's ownership. Decoupled from any single module: a locked write from
 * Tasks, Drive, chat, etc. all surface the same modal.
 *
 * The event name is duplicated as a literal in lib/http.ts (the dispatch site)
 * because lib must not import from features — keep the two in sync.
 */

type LockReason = 'nonpayment' | 'unresolved_ownership'

export function BillingIssueModalHost() {
  const { token, selectedContainer } = useSession()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<LockReason | undefined>(undefined)

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { reason?: LockReason } | undefined
      setReason(detail?.reason)
      setOpen(true)
    }
    window.addEventListener('xavior:workspace-locked', handler)
    return () => window.removeEventListener('xavior:workspace-locked', handler)
  }, [])

  if (!open) return null

  // Unknown ownership during load defaults to owner — matches the billing-surface
  // convention in App.tsx (selectedContainer?.isOwner !== false). A non-owner who
  // slips through still can't open the portal (the route is owner-gated server-side).
  const isOwner = selectedContainer?.isOwner !== false

  return (
    <BillingIssueModal
      open={open}
      context="fix_payment"
      isOwner={isOwner}
      authToken={token}
      reason={reason}
      onClose={() => setOpen(false)}
    />
  )
}
